// src/components/UserManagement/AdminManagementTable.jsx
import React, { useEffect, useState } from "react";
import DataTable from "../DataTable";
import CustomSwitch from "../CommonComponents/CustomSwitch";
import AddAdminForm from "./AddAdminForm";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_ADMINS,
  UPDATE_ADMIN,
  ADD_ADMIN,
  DELETE_ADMIN,
} from "../../graphql/userManagement";
import Loader from "../../Loader";
import { useAuthz } from "../../auth/useAuthz";

const AdminManagementTable = ({
  data, // legacy prop (kept for compatibility) - component uses its own query as source of truth
  addEmployee,
  setAddEmployee,
  editForm,
  setEditForm,
  editData,
  setEditData,
  isFetched, // optional legacy prop
}) => {
  // query admins directly here (single source of truth)
  const {
    data: adminsData,
    loading,
    error,
    refetch,
  } = useQuery(GET_ADMINS, {
    fetchPolicy: "cache-and-network",
  });

  const { can } = useAuthz();
  const admins = adminsData?.getAdminList?.admins || [];

  // local UI state
  const [updatingRowId, setUpdatingRowId] = useState(null);

  // mutations
  const [updateAdminMutation] = useMutation(UPDATE_ADMIN);
  const [deleteAdminMutation] = useMutation(DELETE_ADMIN);

  // open edit form helper
  const handleEditClick = (row) => {
    setEditData(row);
    setEditForm(true);
  };

  // toggle isOnBoarded with optimistic UI and cache update
  const handleToggle = async (row) => {
    setUpdatingRowId(row.id);
    try {
      await updateAdminMutation({
        variables: {
          data: {
            id: row.id,
            isOnBoarded: !row.isOnBoarded,
            fullName: row.fullName,
          },
        },
        optimisticResponse: {
          updateAdmin: {
            __typename: "MutationResponse",
            status: "SUCCESS",
            message: "Optimistic update",
            admin: {
              __typename: "Admin",
              id: row.id,
              fullName: row.fullName,
              contactNumber: row.contactNumber || null,
              email: row.email || null,
              isOnBoarded: !row.isOnBoarded,
            },
          },
        },
        update: (cache, { data }) => {
          const updated = data?.updateAdmin?.admin;
          if (!updated) return;
          try {
            const existing = cache.readQuery({ query: GET_ADMINS });
            if (!existing || !existing.getAdminList) return;
            const next = existing.getAdminList.admins.map((a) =>
              a.id === updated.id ? updated : a
            );
            cache.writeQuery({
              query: GET_ADMINS,
              data: {
                getAdminList: { ...existing.getAdminList, admins: next },
              },
            });
          } catch (err) {
            // if cache ops fail, fallback to refetch
            console.warn("cache update failed, refetching", err);
            refetch();
          }
        },
      });
    } catch (err) {
      console.error("Toggle failed", err);
      // optionally show toast
    } finally {
      setUpdatingRowId(null);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Are you sure you want to delete this admin?");
    if (!ok) return;
    try {
      const res = await deleteAdminMutation({
        variables: { data: { id } },
        update: (cache, { data }) => {
          if (!data?.deleteAdmin?.id) return;
          try {
            const existing = cache.readQuery({ query: GET_ADMINS });
            const next = existing.getAdminList.admins.filter(
              (a) => a.id !== id
            );
            cache.writeQuery({
              query: GET_ADMINS,
              data: {
                getAdminList: { ...existing.getAdminList, admins: next },
              },
            });
          } catch (err) {
            refetch();
          }
        },
      });
      if (res?.data?.deleteAdmin?.status !== "SUCCESS") {
        alert(res?.data?.deleteAdmin?.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete failed", err);
      alert("Delete failed");
    }
  };

  if (loading) return <Loader />;

  return (
    <>
      {addEmployee ? (
        <AddAdminForm
          setAddAdmin={setAddEmployee}
          // after save, refetch to update list (AddAdminForm already updates cache too if available)
          onSaved={() => {
            setAddEmployee(false);
            refetch();
          }}
        />
      ) : editForm ? (
        <AddAdminForm
          setAddAdmin={setEditForm}
          editData={editData}
          onSaved={() => {
            setEditForm(false);
            refetch();
          }}
        />
      ) : (
        <div className="w-full">
          <div className="hidden md:block">
          <DataTable tableName="admin-management">
            <thead>
              <tr>
                <th className="text-white text-left">Name</th>
                <th className="text-white text-left">Mobile Number</th>
                <th className="text-white text-center">Active</th>
                <th className="text-white text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {admins.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-gray-400 p-4">
                    No admins found.
                  </td>
                </tr>
              ) : (
                admins.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b-2 border-b-[#E0E0E0] hover:cursor-pointer"
                  >
                    <td className="text-white px-3 py-3">{row.fullName}</td>
                    <td className="text-white px-3 py-3">
                      {row.contactNumber}
                    </td>
                    <td className="text-white px-3 py-3 text-center">
                      <CustomSwitch
                        checked={row.isOnBoarded}
                        isLoading={updatingRowId === row.id}
                        onChange={() => handleToggle(row)}
                        id={row.id}
                      />
                    </td>
                    <td className="px-3 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {can("adminManagement.edit") && (
                          <button
                            className="rounded-md bg-white py-2 px-3 text-black text-sm font-medium"
                            onClick={() => handleEditClick(row)}
                          >
                            Edit
                          </button>
                        )}
                        {can("adminManagement.delete") && (
                          <button
                            className="rounded-md bg-white py-2 px-3 text-black text-sm font-medium"
                            onClick={() => handleDelete(row.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </DataTable>
          </div>

          {/* Mobile: card view */}
          <div className="md:hidden flex flex-col gap-3">
            {admins.length === 0 ? (
              <div className="text-gray-400 text-sm text-center py-8">
                No admins found.
              </div>
            ) : (
              admins.map((row) => (
                <div
                  key={row.id}
                  className="relative bg-[#0c0b0b] border border-[#2a2a2a] rounded-xl p-4 shadow"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-white font-semibold text-sm break-words">
                      {row.fullName}
                    </span>
                    <CustomSwitch
                      checked={row.isOnBoarded}
                      isLoading={updatingRowId === row.id}
                      onChange={() => handleToggle(row)}
                      id={`mobile-${row.id}`}
                    />
                  </div>

                  <div className="mt-3 text-sm">
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Mobile Number
                    </div>
                    <div className="text-white">{row.contactNumber}</div>
                  </div>

                  {(can("adminManagement.edit") ||
                    can("adminManagement.delete")) && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-[#2a2a2a]">
                      {can("adminManagement.edit") && (
                        <button
                          className="flex-1 rounded-md bg-white py-1.5 px-3 text-black text-sm font-medium"
                          onClick={() => handleEditClick(row)}
                        >
                          Edit
                        </button>
                      )}
                      {can("adminManagement.delete") && (
                        <button
                          className="flex-1 rounded-md bg-white py-1.5 px-3 text-black text-sm font-medium"
                          onClick={() => handleDelete(row.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AdminManagementTable;
